<!--
  移动零部件属性面板组件。

  文件定位：
  - 对应 SolidWorks"移动/旋转零部件 PropertyManager"的 UI 层；
  - 只负责将状态映射为界面，不承担任何几何求解；
  - 所有变更通过 MoveComponentController 回调到 cad-core，与基准轴面板保持同等模式。

  面板区块与 SolidWorks 完全对齐：
  1. 移动(M)   — 输入模式下拉（自由拖动 / 沿装配体 XYZ / 沿实体 / Delta XYZ / 到 XYZ 位置）
  2. 旋转(R)   — 旋转输入模式下拉（自由拖动 / 对于实体 / Delta XYZ）
  3. 选项(P)   — 单选：标准拖动 / 碰撞检查
-->
<template>
  <div class="move-component-property" data-testid="move-component-property-section">
    <n-collapse
      :expanded-names="expandedNames"
      class="pm-collapse"
      @update:expanded-names="handleExpandedNamesChange"
    >

      <!-- 移动(M) -->
      <n-collapse-item title="移动(M)" name="move">
        <div class="group-block">
          <!-- 输入模式选择 -->
          <n-select
            :value="moveInputMode"
            size="small"
            :options="moveInputModeOptions"
            @update:value="handleMoveInputModeChange"
          />

          <template v-if="moveInputMode === MoveComponentInputMode.AlongEntity">
            <div class="field-label">参考实体：</div>
            <div class="selection-box along-entity-box" data-testid="along-entity-reference-box">
              <span v-if="alongEntityReferenceLabel" class="selection-box-text">
                {{ alongEntityReferenceLabel }}
              </span>
              <n-empty v-else size="small" description="请选择基准轴、基准面、特征直线或特征面" />
            </div>
            <div class="along-entity-status">
              <span class="along-entity-phase">
                {{ alongEntityPhaseText }}
              </span>
              <n-button
                size="small"
                secondary
                :disabled="!alongEntityReferenceObjectId"
                @click="handleClearAlongEntityReference"
              >
                清空参考
              </n-button>
            </div>
          </template>

          <!-- ------ Delta XYZ / 到 XYZ 位置输入区 开始------ -->
          <template v-if="moveInputMode === MoveComponentInputMode.DeltaXYZ">
            <div class="xyz-input-list" data-testid="delta-xyz-input-list">
              <div class="xyz-input-row">
                <span class="xyz-input-label">ΔX</span>
                <n-input-number
                  :value="deltaXYZDisplay[0]"
                  size="small"
                  :precision="2"
                  :step="1"
                  @update:value="(value) => handleDeltaXYZAxisChange(0, value)"
                />
                <span class="unit-label">mm</span>
              </div>
              <div class="xyz-input-row">
                <span class="xyz-input-label">ΔY</span>
                <n-input-number
                  :value="deltaXYZDisplay[1]"
                  size="small"
                  :precision="2"
                  :step="1"
                  @update:value="(value) => handleDeltaXYZAxisChange(1, value)"
                />
                <span class="unit-label">mm</span>
              </div>
              <div class="xyz-input-row">
                <span class="xyz-input-label">ΔZ</span>
                <n-input-number
                  :value="deltaXYZDisplay[2]"
                  size="small"
                  :precision="2"
                  :step="1"
                  @update:value="(value) => handleDeltaXYZAxisChange(2, value)"
                />
                <span class="unit-label">mm</span>
              </div>
            </div>
            <n-button size="small" class="apply-move-btn" @click="handleApplyDeltaXYZMove">
              应用(A)
            </n-button>
          </template>

          <template v-if="moveInputMode === MoveComponentInputMode.ToXYZPosition">
            <div class="xyz-input-list" data-testid="target-xyz-input-list">
              <div class="xyz-input-row">
                <span class="xyz-input-label">x</span>
                <n-input-number
                  :value="targetXYZPositionDraft[0]"
                  size="small"
                  :show-button="true"
                  :precision="8"
                  :step="1"
                  @update:value="(value) => handleTargetXYZAxisChange(0, value)"
                  @blur="() => commitTargetXYZAxis(0)"
                  @keyup.enter="() => commitTargetXYZAxis(0)"
                />
                <span class="unit-label">mm</span>
              </div>
              <div class="xyz-input-row">
                <span class="xyz-input-label">y</span>
                <n-input-number
                  :value="targetXYZPositionDraft[1]"
                  size="small"
                  :show-button="true"
                  :precision="8"
                  :step="1"
                  @update:value="(value) => handleTargetXYZAxisChange(1, value)"
                  @blur="() => commitTargetXYZAxis(1)"
                  @keyup.enter="() => commitTargetXYZAxis(1)"
                />
                <span class="unit-label">mm</span>
              </div>
              <div class="xyz-input-row">
                <span class="xyz-input-label">z</span>
                <n-input-number
                  :value="targetXYZPositionDraft[2]"
                  size="small"
                  :show-button="true"
                  :precision="8"
                  :step="1"
                  @update:value="(value) => handleTargetXYZAxisChange(2, value)"
                  @blur="() => commitTargetXYZAxis(2)"
                  @keyup.enter="() => commitTargetXYZAxis(2)"
                />
                <span class="unit-label">mm</span>
              </div>
            </div>
            <n-button size="small" class="apply-move-btn" @click="handleApplyTargetXYZMove">
              应用(A)
            </n-button>
          </template>
          <!-- ------ Delta XYZ / 到 XYZ 位置输入区 结束------ -->
        </div>
      </n-collapse-item>

      <!-- 旋转(R) -->
      <n-collapse-item title="旋转(R)" name="rotate">
        <div class="group-block">
          <n-select
            :value="rotateInputMode"
            size="small"
            :options="rotateInputModeOptions"
            @update:value="handleRotateInputModeChange"
          />

          <!-- ------ 旋转 Delta XYZ 输入区 开始------ -->
          <template v-if="rotateInputMode === MoveComponentRotateInputMode.DeltaXYZ">
            <div class="xyz-input-list" data-testid="rotate-delta-xyz-input-list">
              <div class="xyz-input-row">
                <span class="xyz-input-label">ΔX</span>
                <n-input-number
                  :value="rotateDeltaXYZ[0]"
                  size="small"
                  :precision="2"
                  :step="1"
                  @update:value="(value) => handleRotateDeltaXYZAxisChange(0, value)"
                />
                <span class="unit-label">deg</span>
              </div>
              <div class="xyz-input-row">
                <span class="xyz-input-label">ΔY</span>
                <n-input-number
                  :value="rotateDeltaXYZ[1]"
                  size="small"
                  :precision="2"
                  :step="1"
                  @update:value="(value) => handleRotateDeltaXYZAxisChange(1, value)"
                />
                <span class="unit-label">deg</span>
              </div>
              <div class="xyz-input-row">
                <span class="xyz-input-label">ΔZ</span>
                <n-input-number
                  :value="rotateDeltaXYZ[2]"
                  size="small"
                  :precision="2"
                  :step="1"
                  @update:value="(value) => handleRotateDeltaXYZAxisChange(2, value)"
                />
                <span class="unit-label">deg</span>
              </div>
            </div>
            <n-button size="small" class="apply-rotate-btn" @click="handleApplyRotateDeltaXYZ">
              应用(A)
            </n-button>
          </template>
          <!-- ------ 旋转 Delta XYZ 输入区 结束------ -->
        </div>
      </n-collapse-item>

      <!-- 选项(P) -->
      <n-collapse-item title="选项(P)" name="options">
        <div class="group-block">
          <n-radio-group
            :value="optionMode"
            size="small"
            @update:value="handleOptionModeChange"
          >
            <div class="radio-list">
              <n-radio :value="MoveComponentOptionMode.Standard">标准拖动</n-radio>
              <n-radio :value="MoveComponentOptionMode.CollisionDetection">碰撞检查</n-radio>
            </div>
          </n-radio-group>

          <!-- ------ 碰撞检查范围选项 开始------ -->
          <template v-if="optionMode === MoveComponentOptionMode.CollisionDetection">
            <div class="field-label">检查范围:</div>
            <n-radio-group
              :value="collisionCheckScope"
              size="small"
              @update:value="handleCollisionCheckScopeChange"
            >
              <div class="radio-list">
                <n-radio :value="MoveComponentCollisionCheckScope.AllComponents">所有零部件之间(L)</n-radio>
                <n-radio :value="MoveComponentCollisionCheckScope.SelectedComponents">这些零部件之间(H)</n-radio>
              </div>
            </n-radio-group>

            <template v-if="collisionCheckScope === MoveComponentCollisionCheckScope.SelectedComponents">
              <div
                class="collision-components-box"
                :class="{ 'collision-components-box--active': isCollisionComponentSelectionActive }"
                data-testid="collision-check-components-box"
                @click="handleActivateCollisionComponentsInput"
              >
                <SelectionListFrame
                  :items="collisionComponentItems"
                  :active-index="isCollisionComponentSelectionActive ? 0 : -1"
                  item-key-field="objectId"
                  empty-text=""
                  :visible-rows="1"
                  :context-menu-enabled="true"
                  delete-label="消除选中"
                  delete-all-label="消除选中"
                  @update:active-index="handleActivateCollisionComponentsInput"
                  @delete-item="handleDeleteCollisionComponentItem"
                  @delete-all="handleDeleteAllCollisionComponents"
                >
                  <template #item="{ item }">
                    <span class="selection-box-text">{{ resolveCollisionComponentItemLabel(item) }}</span>
                  </template>
                </SelectionListFrame>
              </div>
              <n-button
                size="small"
                secondary
                class="resume-drag-btn"
                :disabled="!canResumeCollisionDrag"
                @click="handleResumeCollisionDrag"
              >
                恢复拖动(U)
              </n-button>
            </template>

            <n-checkbox
              :checked="collisionStopAtCollision"
              size="small"
              @update:checked="(checked) => handleCollisionStopAtCollisionChange(Boolean(checked))"
            >
              碰撞时停止(T)
            </n-checkbox>
            <n-checkbox
              :checked="collisionDraggedPartOnly"
              size="small"
              @update:checked="(checked) => handleCollisionDraggedPartOnlyChange(Boolean(checked))"
            >
              仅被拖动的零件(G)
            </n-checkbox>
          </template>
          <!-- ------ 碰撞检查范围选项 结束------ -->
        </div>
      </n-collapse-item>

    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  convertMToMm,
  convertMmToM,
  MoveAlongEntitySelectionPhase,
  roundNullableToEightDecimalPlaces,
  roundToEightDecimalPlaces,
  type MoveComponentController,
} from '@puppycad/cad-core';
import {
  NButton,
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NEmpty,
  NInputNumber,
  NRadio,
  NRadioGroup,
  NSelect,
} from 'naive-ui';
import {
  MoveComponentCollisionCheckScope,
  MoveComponentCollisionSelectionPhase,
  MoveComponentInputMode,
  MoveComponentOptionMode,
  MoveComponentRotateInputMode,
} from '@/core';
import type { PropertyManagerContext } from '@/core';
import { useViewer } from '@/hooks';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';

/** 组件 Props，仅传入 context，与其他属性面板保持一致。 */
interface MoveComponentPropertySectionProps {
  /** 属性面板上下文。 */
  context: PropertyManagerContext;
}

const props = defineProps<MoveComponentPropertySectionProps>();
const { getViewer } = useViewer();

// ─── 默认展开的区块 ──────────────────────────────────────────
const DEFAULT_SECONDARY_EXPANDED_NAMES = ['options'] as const;

/**
 * 解析当前主区块名称。
 *
 * 设计说明：
 * 1. 移动与旋转在 SolidWorks 中属于互斥输入主态；
 * 2. 当前命令为旋转时默认展开旋转，否则默认展开移动；
 * 3. 其它区块保持常开，不参与互斥逻辑。
 *
 * @returns 主区块名称。
 */
function resolvePrimaryExpandedName(): 'move' | 'rotate' {
  return props.context.activeCommand === 'assembly-rotate-component' ? 'rotate' : 'move';
}

/**
 * 创建默认展开项集合。
 *
 * @returns 展开项列表。
 */
function createExpandedNames(): string[] {
  return [resolvePrimaryExpandedName(), ...DEFAULT_SECONDARY_EXPANDED_NAMES];
}

const expandedNames = ref<string[]>(createExpandedNames());
const targetXYZPositionDraft = ref<[number | null, number | null, number | null]>([0, 0, 0]);
const isTargetXYZDraftEditing = ref(false);

// ─── 本地快照（通过轮询从控制器同步，与基准轴保持同等策略） ──────────
interface LocalSnapshot {
  moveInputMode: MoveComponentInputMode;
  alongEntityReferenceObjectId: string | null;
  alongEntityReferenceLabel: string | null;
  alongEntitySelectionPhase: MoveAlongEntitySelectionPhase;
  deltaXYZ: readonly [number, number, number];
  targetXYZPosition: readonly [number, number, number];
  rotateInputMode: MoveComponentRotateInputMode;
  rotateDeltaXYZ: readonly [number, number, number];
  optionMode: MoveComponentOptionMode;
  collisionCheckScope: MoveComponentCollisionCheckScope;
  collisionCheckComponentObjectIds: readonly string[];
  collisionSelectionPhase: MoveComponentCollisionSelectionPhase;
  collisionStopAtCollision: boolean;
  collisionDraggedPartOnly: boolean;
}

/** 碰撞检查零部件选择列表项。 */
interface CollisionComponentItem {
  /** 零部件对象 ID。 */
  objectId: string;
  /** 零部件显示名。 */
  label: string;
}

const localSnapshot = ref<LocalSnapshot>({
  moveInputMode: MoveComponentInputMode.FreeDrag,
  alongEntityReferenceObjectId: null,
  alongEntityReferenceLabel: null,
  alongEntitySelectionPhase: MoveAlongEntitySelectionPhase.AwaitingReference,
  deltaXYZ: [0, 0, 0],
  targetXYZPosition: [0, 0, 0],
  rotateInputMode: MoveComponentRotateInputMode.FreeDrag,
  rotateDeltaXYZ: [0, 0, 0],
  optionMode: MoveComponentOptionMode.Standard,
  collisionCheckScope: MoveComponentCollisionCheckScope.AllComponents,
  collisionCheckComponentObjectIds: [],
  collisionSelectionPhase: MoveComponentCollisionSelectionPhase.Dragging,
  collisionStopAtCollision: false,
  collisionDraggedPartOnly: false,
});

const collisionComponentItems = ref<CollisionComponentItem[]>([]);

/**
 * 处理折叠面板展开项变更。
 *
 * @param names 最新展开项集合。
 */
function handleExpandedNamesChange(names: Array<string | number>): void {
  const normalizedNames = names.map((name) => String(name));
  const includesMove = normalizedNames.includes('move');
  const includesRotate = normalizedNames.includes('rotate');

  if (includesMove && includesRotate) {
    const moveIndex = normalizedNames.lastIndexOf('move');
    const rotateIndex = normalizedNames.lastIndexOf('rotate');
    const preferredPrimaryName = rotateIndex > moveIndex ? 'rotate' : 'move';
    expandedNames.value = normalizedNames.filter((name) => {
      if (name === 'move' || name === 'rotate') {
        return name === preferredPrimaryName;
      }
      return true;
    });
    // 切换主模式时同步命令，使 PropertyManager 标题实时刷新。
    const switchCommand = preferredPrimaryName === 'rotate'
      ? 'assembly-rotate-component'
      : 'assembly-move-component';
    props.context.onSwitchActiveCommand?.(switchCommand);
    return;
  }

  expandedNames.value = normalizedNames;
}

watch(
  () => props.context.activeCommand,
  () => {
    const nextPrimaryName = resolvePrimaryExpandedName();
    expandedNames.value = [
      nextPrimaryName,
      ...expandedNames.value.filter((name) => name !== 'move' && name !== 'rotate'),
    ];
  },
  { immediate: true },
);

/**
 * 获取移动零部件控制器实例。
 */
function getMoveComponentController(): MoveComponentController | null {
  const runtime = getViewer()
  return runtime?.moveComponentController ?? null;
}

/**
 * 将目标 XYZ 快照同步到输入草稿值。
 *
 * @param value 当前目标 XYZ 快照。
 */
function syncTargetXYZDraft(value: readonly [number, number, number]): void {
  targetXYZPositionDraft.value = [
    roundNullableToEightDecimalPlaces(convertMToMm(value[0])),
    roundNullableToEightDecimalPlaces(convertMToMm(value[1])),
    roundNullableToEightDecimalPlaces(convertMToMm(value[2])),
  ];
}

//------ 移动旋转零部件碰撞检查选项 开始------
/**
 * 同步碰撞检查零部件选择列表。
 *
 * @param objectIds 零部件对象 ID 列表。
 */
function syncCollisionComponentItems(objectIds: readonly string[]): void {
  collisionComponentItems.value = objectIds.map((objectId) => ({
    objectId,
    label: resolveCollisionComponentLabel(objectId),
  }));
}

/**
 * 解析碰撞检查零部件显示名称。
 *
 * @param objectId 零部件对象 ID。
 * @returns 可展示名称。
 */
function resolveCollisionComponentLabel(objectId: string): string {
  const object = props.context.renderer?.findSceneObjectById?.(objectId) ?? null;
  const importedName = typeof object?.userData?.glbHierarchy?.name === 'string'
    ? object.userData.glbHierarchy.name.trim()
    : '';
  if (importedName.length > 0) {
    return importedName;
  }

  const objectName = object?.name?.trim() ?? '';
  return objectName.length > 0 ? objectName : objectId;
}
//------ 移动旋转零部件碰撞检查选项 结束------

/**
 * 从控制器同步当前快照到本地状态。
 */
function syncFromController(): void {
  const controller = getMoveComponentController();
  const viewModel = controller?.getViewModel?.();
  const state = viewModel?.state;
  if (!state) {
    return;
  }

  localSnapshot.value = {
    moveInputMode: state.moveInputMode ?? localSnapshot.value.moveInputMode,
    alongEntityReferenceObjectId: state.alongEntityReferenceObjectId ?? localSnapshot.value.alongEntityReferenceObjectId,
    alongEntityReferenceLabel: state.alongEntityReferenceLabel ?? localSnapshot.value.alongEntityReferenceLabel,
    alongEntitySelectionPhase: state.alongEntitySelectionPhase ?? localSnapshot.value.alongEntitySelectionPhase,
    deltaXYZ: state.deltaXYZ ?? localSnapshot.value.deltaXYZ,
    targetXYZPosition: state.targetXYZPosition ?? localSnapshot.value.targetXYZPosition,
    rotateInputMode: state.rotateInputMode ?? localSnapshot.value.rotateInputMode,
    rotateDeltaXYZ: state.rotateDeltaXYZ ?? localSnapshot.value.rotateDeltaXYZ,
    optionMode: state.optionMode ?? localSnapshot.value.optionMode,
    collisionCheckScope: state.collisionCheckScope ?? localSnapshot.value.collisionCheckScope,
    collisionCheckComponentObjectIds: state.collisionCheckComponentObjectIds ?? localSnapshot.value.collisionCheckComponentObjectIds,
    collisionSelectionPhase: state.collisionSelectionPhase ?? localSnapshot.value.collisionSelectionPhase,
    collisionStopAtCollision: state.collisionStopAtCollision ?? localSnapshot.value.collisionStopAtCollision,
    collisionDraggedPartOnly: state.collisionDraggedPartOnly ?? localSnapshot.value.collisionDraggedPartOnly,
  };
  syncCollisionComponentItems(localSnapshot.value.collisionCheckComponentObjectIds);

  if (!isTargetXYZDraftEditing.value) {
    syncTargetXYZDraft(localSnapshot.value.targetXYZPosition);
  }
}

let syncTimer: number | null = null;

onMounted(() => {
  syncFromController();
  /** 120ms 轮询同步，与基准轴面板保持一致。 */
  syncTimer = window.setInterval(() => {
    syncFromController();
  }, 120);
});

onBeforeUnmount(() => {
  if (syncTimer !== null) {
    clearInterval(syncTimer);
    syncTimer = null;
  }
});

// ─── 计算属性 ─────────────────────────────────────────────────
const moveInputMode = computed(() => localSnapshot.value.moveInputMode);
const alongEntityReferenceObjectId = computed(() => localSnapshot.value.alongEntityReferenceObjectId);
const alongEntityReferenceLabel = computed(() => localSnapshot.value.alongEntityReferenceLabel);
const alongEntitySelectionPhase = computed(() => localSnapshot.value.alongEntitySelectionPhase);
const deltaXYZ = computed(() => localSnapshot.value.deltaXYZ);
/** Delta XYZ 的毫米显示值。 */
const deltaXYZDisplay = computed<readonly [number, number, number]>(() => [
  roundToEightDecimalPlaces(convertMToMm(deltaXYZ.value[0])),
  roundToEightDecimalPlaces(convertMToMm(deltaXYZ.value[1])),
  roundToEightDecimalPlaces(convertMToMm(deltaXYZ.value[2])),
]);
const rotateInputMode = computed(() => localSnapshot.value.rotateInputMode);
const rotateDeltaXYZ = computed(() => localSnapshot.value.rotateDeltaXYZ);
const optionMode = computed(() => localSnapshot.value.optionMode);
const collisionCheckScope = computed(() => localSnapshot.value.collisionCheckScope);
const collisionSelectionPhase = computed(() => localSnapshot.value.collisionSelectionPhase);
const collisionStopAtCollision = computed(() => localSnapshot.value.collisionStopAtCollision);
const collisionDraggedPartOnly = computed(() => localSnapshot.value.collisionDraggedPartOnly);
const isCollisionComponentSelectionActive = computed(() => (
  collisionSelectionPhase.value === MoveComponentCollisionSelectionPhase.SelectingComponents
));
const canResumeCollisionDrag = computed(() => collisionComponentItems.value.length > 0);

// ─── 移动输入模式选项 ─────────────────────────────────────────
const moveInputModeOptions = [
  { label: '自由拖动', value: MoveComponentInputMode.FreeDrag },
  { label: '沿装配体 XYZ', value: MoveComponentInputMode.AlongAssemblyAxis },
  { label: '沿实体', value: MoveComponentInputMode.AlongEntity },
  { label: '由 Delta XYZ', value: MoveComponentInputMode.DeltaXYZ },
  { label: '到 XYZ 位置', value: MoveComponentInputMode.ToXYZPosition },
];

// ─── 旋转输入模式选项 ─────────────────────────────────────────
const rotateInputModeOptions = [
  { label: '自由拖动', value: MoveComponentRotateInputMode.FreeDrag },
  { label: '对于实体', value: MoveComponentRotateInputMode.AroundEntity },
  { label: '由 Delta XYZ', value: MoveComponentRotateInputMode.DeltaXYZ },
];

/**
 * 解析沿实体当前提示文本。
 */
const alongEntityPhaseText = computed(() => (
  alongEntitySelectionPhase.value === MoveAlongEntitySelectionPhase.AwaitingComponent
    ? '已选参考实体，请选择零部件并拖动'
    : '请先选择参考实体'
));

// ─── 事件处理 ─────────────────────────────────────────────────

/**
 * 切换移动输入模式。
 * @param mode 目标模式。
 */
function handleMoveInputModeChange(mode: MoveComponentInputMode): void {
  localSnapshot.value.moveInputMode = mode;
  getMoveComponentController()?.setMoveInputMode?.(mode);
  syncFromController();

  if (mode === MoveComponentInputMode.ToXYZPosition) {
    isTargetXYZDraftEditing.value = false;
    syncTargetXYZDraft(localSnapshot.value.targetXYZPosition);
  }
}

/**
 * 清空沿实体参考实体。
 */
function handleClearAlongEntityReference(): void {
  getMoveComponentController()?.clearAlongEntityReference?.();
  getViewer()?.clearSelectionProgrammatically?.();
  syncFromController();
}

/**
 * 更新 Delta XYZ 某一轴输入值。
 * @param axisIndex 轴索引。
 * @param value 输入值。
 */
function handleDeltaXYZAxisChange(axisIndex: 0 | 1 | 2, value: number | null): void {
  if (value === null) {
    return;
  }
  const nextDelta = [...localSnapshot.value.deltaXYZ] as [number, number, number];
  // UI 输入值是毫米，控制器保存和计算使用米。
  nextDelta[axisIndex] = convertMmToM(value);
  localSnapshot.value.deltaXYZ = nextDelta;
  getMoveComponentController()?.setDeltaXYZ?.(nextDelta);
}

/**
 * 更新目标 XYZ 某一轴输入值。
 * @param axisIndex 轴索引。
 * @param value 输入值。
 */
function handleTargetXYZAxisChange(axisIndex: 0 | 1 | 2, value: number | null): void {
  isTargetXYZDraftEditing.value = true;
  const nextDraft = [...targetXYZPositionDraft.value] as [number | null, number | null, number | null];
  nextDraft[axisIndex] = roundNullableToEightDecimalPlaces(value);
  targetXYZPositionDraft.value = nextDraft;
}

/**
 * 提交指定目标 XYZ 轴的草稿值。
 * @param axisIndex 轴索引。
 */
function commitTargetXYZAxis(axisIndex: 0 | 1 | 2): void {
  const draftValue = targetXYZPositionDraft.value[axisIndex];
  if (draftValue === null || !Number.isFinite(draftValue)) {
    isTargetXYZDraftEditing.value = false;
    syncTargetXYZDraft(localSnapshot.value.targetXYZPosition);
    return;
  }

  const nextPosition = [...localSnapshot.value.targetXYZPosition] as [number, number, number];
  // 目标坐标草稿以毫米显示，提交给控制器前转换为米。
  nextPosition[axisIndex] = roundToEightDecimalPlaces(convertMmToM(draftValue));
  localSnapshot.value.targetXYZPosition = nextPosition;
  getMoveComponentController()?.setTargetXYZPosition?.(nextPosition);
  isTargetXYZDraftEditing.value = false;
  syncTargetXYZDraft(nextPosition);
}

/**
 * 应用 Delta XYZ 移动预览。
 */
function handleApplyDeltaXYZMove(): void {
  getMoveComponentController()?.applyDeltaXYZMove?.();
}

/**
 * 应用到 XYZ 位置预览。
 */
function handleApplyTargetXYZMove(): void {
  getMoveComponentController()?.applyMoveToXYZPosition?.();
}

/**
 * 切换旋转输入模式。
 * @param mode 目标模式。
 */
function handleRotateInputModeChange(mode: MoveComponentRotateInputMode): void {
  localSnapshot.value.rotateInputMode = mode;
  getMoveComponentController()?.setRotateInputMode?.(mode);
  syncFromController();
}

/**
 * 更新旋转 Delta XYZ 某一轴输入值。
 * @param axisIndex 轴索引。
 * @param value 输入值。
 */
function handleRotateDeltaXYZAxisChange(axisIndex: 0 | 1 | 2, value: number | null): void {
  if (value === null) {
    return;
  }
  const nextDeltaRotation = [...localSnapshot.value.rotateDeltaXYZ] as [number, number, number];
  nextDeltaRotation[axisIndex] = value;
  localSnapshot.value.rotateDeltaXYZ = nextDeltaRotation;
  getMoveComponentController()?.setRotateDeltaXYZ?.(nextDeltaRotation);
}

/**
 * 应用旋转 Delta XYZ 预览。
 */
function handleApplyRotateDeltaXYZ(): void {
  getMoveComponentController()?.applyDeltaXYZRotate?.();
}

/**
 * 切换运动选项模式。
 * @param mode 目标选项模式。
 */
function handleOptionModeChange(mode: MoveComponentOptionMode): void {
  localSnapshot.value.optionMode = mode;
  getMoveComponentController()?.setOptionMode?.(mode);
}

//------ 移动旋转零部件碰撞检查选项 开始------
/**
 * 切换碰撞检查范围。
 *
 * @param scope 目标检查范围。
 */
function handleCollisionCheckScopeChange(scope: MoveComponentCollisionCheckScope): void {
  localSnapshot.value.collisionCheckScope = scope;
  getMoveComponentController()?.setCollisionCheckScope?.(scope);
  syncFromController();
}

/**
 * 激活“供碰撞检查的零部件”选择框。
 */
function handleActivateCollisionComponentsInput(): void {
  getMoveComponentController()?.activateCollisionCheckComponentSelection?.();
  syncFromController();
}

/**
 * 恢复移动或旋转拖动。
 */
function handleResumeCollisionDrag(): void {
  getMoveComponentController()?.resumeCollisionDrag?.();
  getViewer()?.clearSelectionProgrammatically?.();
  syncFromController();
}

/**
 * 切换“碰撞时停止”。
 *
 * @param checked 是否勾选。
 */
function handleCollisionStopAtCollisionChange(checked: boolean): void {
  localSnapshot.value.collisionStopAtCollision = checked;
  getMoveComponentController()?.setCollisionStopAtCollision?.(checked);
}

/**
 * 切换“仅被拖动的零件”。
 *
 * @param checked 是否勾选。
 */
function handleCollisionDraggedPartOnlyChange(checked: boolean): void {
  localSnapshot.value.collisionDraggedPartOnly = checked;
  getMoveComponentController()?.setCollisionDraggedPartOnly?.(checked);
}

/**
 * 删除单个供碰撞检查的零部件。
 *
 * @param payload 删除事件载荷。
 */
function handleDeleteCollisionComponentItem(payload: { item: unknown; index: number }): void {
  const item = payload.item as CollisionComponentItem;
  getMoveComponentController()?.removeCollisionCheckComponentObjectId?.(item.objectId);
  getViewer()?.clearSelectionProgrammatically?.();
  syncFromController();
}

/**
 * 清空供碰撞检查的零部件。
 */
function handleDeleteAllCollisionComponents(): void {
  getMoveComponentController()?.clearCollisionCheckComponentObjectIds?.();
  getViewer()?.clearSelectionProgrammatically?.();
  syncFromController();
}

/**
 * 解析选择列表项显示名称。
 *
 * @param item 列表项。
 * @returns 显示名称。
 */
function resolveCollisionComponentItemLabel(item: unknown): string {
  const collisionItem = item as CollisionComponentItem;
  return collisionItem.label || collisionItem.objectId;
}
//------ 移动旋转零部件碰撞检查选项 结束------

</script>

<style scoped>
.move-component-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--n-text-color, #1f2937);
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.group-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.xyz-input-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.xyz-input-row {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  gap: 6px;
  align-items: center;
}

.xyz-input-label {
  font-size: 12px;
  color: var(--n-text-color-2, #4b5563);
  white-space: nowrap;
}

.apply-move-btn {
  align-self: flex-start;
}

.apply-rotate-btn {
  align-self: flex-start;
}

/* 选项单选列表 */
.radio-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

:deep(.n-radio .n-radio__label) {
  color: var(--n-text-color, #1f2937);
}

.field-label {
  font-size: 12px;
  color: var(--n-text-color-3, #6b7280);
}

/* 零部件选择框 */
.selection-box {
  border: 1px solid var(--n-border-color, #d9dee8);
  border-radius: 4px;
  min-height: 56px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.along-entity-box {
  min-height: 48px;
}

.collision-components-box {
  min-width: 0;
}

.collision-components-box :deep(.selection-list-frame) {
  min-height: 34px;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.collision-components-box :deep(.selection-list-frame::-webkit-scrollbar) {
  width: 0;
  height: 0;
}

.collision-components-box--active :deep(.selection-list-frame) {
  border-color: var(--n-primary-color, var(--puppy-primary));
  background: color-mix(in srgb, var(--n-primary-color, var(--puppy-primary)) 8%, transparent);
}

.resume-drag-btn {
  align-self: center;
}

.selection-box-text {
  font-size: 12px;
  color: var(--n-text-color, #1f2937);
  word-break: break-all;
}

.along-entity-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.along-entity-phase {
  font-size: 12px;
  color: var(--n-text-color-3, #6b7280);
}

.unit-label {
  font-size: 12px;
  color: var(--n-text-color-3, #6b7280);
  white-space: nowrap;
}
</style>
